import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Switch,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../../components/utils/Copyable';
import TopBar from '../../../../../../components/utils/TopBar';
import NA from '../../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  GetCollectionCountryCurrencyXrefBIZcoreCC,
  UpdateCollectionCountryCurrencyXrefBIZcoreCC,
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import CollectionCountryCurrencyXrefModal from './CollectionCountryCurrencyXrefModal';

import global from "../../../../../../global.scss";

const CollectionCountryCurrencyXrefPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.cc"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [collectionCountryCurrencyXrefModalOpen, setCollectionCountryCurrencyXrefModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [collectionCountryCurrencyXrefList, setCollectionCountryCurrencyXrefList] = useState([]);
  const [collectionCountryCurrencyXrefTotal, setCollectionCountryCurrencyXrefTotal] = useState(0);

  const [collectionCountryCurrencyXrefMenuOpen, setCollectionCountryCurrencyXrefMenuOpen] = useState(false);
  const [collectionCountryCurrencyXrefMenuAnchor, setCollectionCountryCurrencyXrefMenuAnchor] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Collection Country",
      sort: "ref_collection_country_id",
    },
    {
      label: "Collection Currency",
      sort: "ref_collection_currency_id",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    handleGetCollectionCountryCurrencyXref();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatCollectionCountryCurrencyXrefList(state.collectionCountryCurrencyXrefList.records || []);
      setCollectionCountryCurrencyXrefTotal(state.collectionCountryCurrencyXrefList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatCollectionCountryCurrencyXrefList = (list) => {
    setCollectionCountryCurrencyXrefList(list)
  };

  async function handleGetCollectionCountryCurrencyXref(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetCollectionCountryCurrencyXrefBIZcoreCC(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  const handleToggleCollectionCountryCurrencyXref = (id, bool) => {
    let obj = {
      is_active: bool
    };
    dispatch(UpdateCollectionCountryCurrencyXrefBIZcoreCC(id, obj, setLoading)).then(() => {
      handleGetCollectionCountryCurrencyXref()
    })
  };

  return (
    <Box>

      <CollectionCountryCurrencyXrefModal 
        open={collectionCountryCurrencyXrefModalOpen}
        setOpen={setCollectionCountryCurrencyXrefModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetCollectionCountryCurrencyXref}
        />

      <TopBar
        title={"Collection Country Currency Xref"}
        search={search}
        searchPlaceholder={"Search id, country or currency"}
        onSearchChange={handleSearchChange}
        button1Text={"Add Collection Country Currency Xref"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Collection Country Currency Xref");
          setCollectionCountryCurrencyXrefModalOpen(!collectionCountryCurrencyXrefModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          collectionCountryCurrencyXrefList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No collection country currency xref found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                collectionCountryCurrencyXrefList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      ({row?.ref_collection_country_id?.ref_country_id?.code})&nbsp;{row?.ref_collection_country_id?.ref_country_id?.name}
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row?.ref_collection_country_id?.ref_country_id?.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      ({row?.ref_collection_currency_id?.ref_currency_id?.code})&nbsp;{row?.ref_collection_currency_id?.ref_currency_id?.name}
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row?.ref_collection_currency_id?.ref_currency_id?.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.is_active}
                        onChange={(e) => {
                          handleToggleCollectionCountryCurrencyXref(row.id, e.target.checked);
                        }}
                        />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setCollectionCountryCurrencyXrefMenuAnchor(e.currentTarget);
                          setCollectionCountryCurrencyXrefMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={collectionCountryCurrencyXrefTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={collectionCountryCurrencyXrefMenuOpen}
        anchorEl={collectionCountryCurrencyXrefMenuAnchor}
        onClose={() => setCollectionCountryCurrencyXrefMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setCollectionCountryCurrencyXrefMenuOpen(false);
            setCollectionCountryCurrencyXrefModalOpen(!collectionCountryCurrencyXrefModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default CollectionCountryCurrencyXrefPage;